import React, { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { LanguageCode } from '../types';
import { LANGUAGES, TRANSLATIONS } from '../constants';
import { getBlogPosts } from '../utils/blogLoader';
import { buildSeoMetadata } from '../utils/seo';

export const SEO: React.FC = () => {
  const { lang, slug } = useParams<{ lang: LanguageCode; slug?: string }>();
  const location = useLocation();
  const currentLangCode =
    lang && LANGUAGES[lang as LanguageCode] ? (lang as LanguageCode) : 'en';

  const posts = useMemo(() => getBlogPosts(), []);
  const meta = useMemo(
    () =>
      buildSeoMetadata({
        lang: currentLangCode,
        pathname: location.pathname,
        slug,
        posts,
      }),
    [currentLangCode, location.pathname, posts, slug]
  );

  useEffect(() => {
    const translation = TRANSLATIONS[currentLangCode];
    const isGeneratorPage =
      meta.cleanPath.includes('/qrcode-generator') ||
      meta.cleanPath.includes('/text-to-qrcode') ||
      meta.cleanPath.includes('/link-to-qrcode');
    const isScannerPage = meta.cleanPath.includes('/qrcode-scanner');

    document.title = meta.title;
    document.documentElement.lang = meta.lang;

    const updateMeta = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const upsertLink = (rel: string, href: string, hreflang?: string) => {
      let selector = `link[rel="${rel}"]`;
      if (hreflang) {
        selector += `[hreflang="${hreflang}"]`;
      }
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        if (hreflang) {
          element.setAttribute('hreflang', hreflang);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    updateMeta('description', meta.description);
    updateMeta('keywords', meta.keywords.join(', '));
    updateMeta('robots', 'index, follow');

    updateMeta('og:site_name', 'YesQRCode', 'property');
    updateMeta('og:type', 'website', 'property');
    updateMeta('og:title', meta.title, 'property');
    updateMeta('og:description', meta.description, 'property');
    updateMeta('og:url', meta.canonical, 'property');
    updateMeta('og:image', meta.image, 'property');

    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', meta.title);
    updateMeta('twitter:description', meta.description);
    updateMeta('twitter:image', meta.image);

    upsertLink('canonical', meta.canonical);
    meta.alternates.forEach((alt) => upsertLink('alternate', alt.href, alt.lang));
    upsertLink('alternate', `https://yesqrcode.com/en${meta.cleanPath === '/' ? '' : meta.cleanPath}`, 'x-default');

    const jsonLdGraph: any[] = [
      {
        '@type': 'WebSite',
        '@id': 'https://yesqrcode.com/#website',
        name: 'YesQRCode',
        url: 'https://yesqrcode.com',
        inLanguage: currentLangCode,
      },
      {
        '@type': 'WebPage',
        '@id': `${meta.canonical}#webpage`,
        url: meta.canonical,
        name: meta.title,
        description: meta.description,
        isPartOf: { '@id': 'https://yesqrcode.com/#website' },
        inLanguage: currentLangCode,
      },
    ];

    if (isGeneratorPage || isScannerPage) {
      jsonLdGraph.push({
        '@type': 'SoftwareApplication',
        name: isScannerPage ? translation.pageTitles.scanner : translation.pageTitles.generator,
        applicationCategory: isScannerPage ? 'Tool' : 'UtilitiesApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        url: meta.canonical,
        description: meta.description,
      });
    }

    if (isGeneratorPage && translation.generator?.faq?.items?.length) {
      jsonLdGraph.push({
        '@type': 'FAQPage',
        mainEntity: translation.generator.faq.items.slice(0, 4).map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      });
    }

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': jsonLdGraph,
    };

    let jsonLdScript = document.getElementById('ld-json') as HTMLScriptElement | null;
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.id = 'ld-json';
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(jsonLd);
  }, [meta]);

  return null;
};
